import { ArrowRight, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { navigation } from "../data/site";
import { useI18n } from "../i18n";
import { userApi, type AccountUser } from "../lib/api";
import { Seo } from "./Seo";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { LanguageSwitcher } from "./LanguageSwitcher";
import legalDocuments from "../../../../clients/flutter/legal/documents.json";

function Logo() {
  const { t } = useI18n();
  return (
    <Link className="brand" to="/" aria-label="Flux cloud">
      <img src="/logo.png" alt="Flux cloud" className="brand-logo-img" />
      <span className="brand-text">
        <span className="brand-title">Flux cloud</span>
        <small className="brand-tag">{t("云流")}</small>
      </span>
    </Link>
  );
}

export function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<AccountUser | null>(null);
  const location = useLocation();
  const { t } = useI18n();

  useScrollReveal();

  useEffect(() => {
    let active = true;
    userApi.me().then((user) => {
      if (active) setCurrentUser(user);
    }).catch(() => {
      if (active) setCurrentUser(null);
    });
    return () => { active = false; };
  }, [location.pathname]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="site-shell">
      <Seo />
      <a className="skip-link" href="#main-content">
        {t("跳到主要内容")}
      </a>
      <div className="announcement-bar">
        <Link to="/account">
          {t("注册后免费加速")}
          <strong>{t("立即体验")} <ArrowRight /></strong>
        </Link>
      </div>
      <header className="site-header">
        <div className="container header-inner">
          <Logo />
          <nav className="desktop-nav" aria-label={t("主导航")}>
            {navigation.map((item) => (
              <NavLink key={item.href} to={item.href} end={item.href === "/"}>
                {t(item.label)}
              </NavLink>
            ))}
          </nav>
          <div className="header-actions">
            <LanguageSwitcher />
            {currentUser ? (
              <Link className="header-account-pill" to="/account" title={currentUser.email}>
                <span className="header-account-avatar">
                  {(currentUser.displayName || currentUser.email).slice(0, 1).toUpperCase()}
                </span>
                <span className="header-account-email">{currentUser.email}</span>
              </Link>
            ) : (
              <>
                <Link className="text-button" to="/account">
                  {t("登录")}
                </Link>
                <Link className="button button-small" to="/account">
                  {t("免费注册")}
                </Link>
              </>
            )}
          </div>
          <button
            className="menu-button"
            type="button"
            aria-label={t(menuOpen ? "关闭菜单" : "打开菜单")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {menuOpen && (
          <nav className="mobile-nav" aria-label={t("移动端导航")}>
            <div className="container">
              {navigation.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === "/"}
                  onClick={closeMenu}
                >
                  {t(item.label)}
                </NavLink>
              ))}
              {currentUser ? (
                <Link className="button button-secondary mobile-nav-login" to="/account" onClick={closeMenu}>
                  {currentUser.email}
                </Link>
              ) : (
                <>
                  <Link className="button button-secondary mobile-nav-login" to="/account" onClick={closeMenu}>
                    {t("登录账户")}
                  </Link>
                  <Link className="button mobile-nav-cta" to="/account" onClick={closeMenu}>
                    {t("免费注册")}
                  </Link>
                </>
              )}
              <LanguageSwitcher className="mobile-language-switch" />
            </div>
          </nav>
        )}
      </header>

      <main id="main-content" key={location.pathname}>
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="container footer-main">
          <div className="footer-brand">
            <Logo />
            <p>{t("让海外华人简单、稳定地连接熟悉的国内内容。")}</p>
          </div>
          <div>
            <h2>{t("产品")}</h2>
            <Link to="/pricing">{t("会员")}</Link>
            <Link to="/download">{t("下载客户端")}</Link>
          </div>
          <div>
            <h2>{t("了解我们")}</h2>
            <a href={legalDocuments.contactEmail ? `mailto:${legalDocuments.contactEmail}` : "/privacy#contact"}>{t("联系支持")}</a>
            <Link to="/terms">{t("用户协议")}</Link>
            <Link to="/privacy">{t("隐私政策")}</Link>
            <a href="/llms.txt">{t("AI 内容说明")}</a>
            <a href="/sitemap.xml">{t("站点地图")}</a>
          </div>
          <div>
            <h2>{t("服务状态")}</h2>
            <span className="status-label"><i /> {t("官网与账户服务正常")}</span>
            <p>{t("支持 Windows、macOS、iOS 与 Android。")}</p>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© {new Date().getFullYear()} 云流加速器</span>
          <span>{t("智能分流 · 多端可用 · 线路透明")}</span>
        </div>
      </footer>
    </div>
  );
}
